import { isNative } from '@unifyapps/ui/utils/isNative';
import type { DocumentPickerAsset } from 'expo-document-picker'; // File Type in native
import { addReferenceIdToFileName } from '../../../hooks/useUppy/getFileName';
import {
  checkFileProtection,
  isSupportedForProtectionCheck,
} from '../../../hooks/useUppy/utils/checkFileProtection';
import type { FileInputProps } from '../types';
import { getFileData } from './getFileData';
import { getRelativePath } from './getRelativePath';

// OS folder-metadata junk swept in with an "upload folder" (macOS `.DS_Store` + AppleDouble `._*`,
// Windows `Thumbs.db` / `desktop.ini`) that's never a real upload.
const OS_METADATA_FILES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);
const isOsMetadataFile = (name: string) => OS_METADATA_FILES.has(name) || name.startsWith('._');

// Only files coming from a folder selection carry a relative path — `webkitRelativePath` (native
// folder input) or `relativePath` (folder drag-drop). An individually-picked file has neither, so
// we only auto-drop the OS-metadata junk when it was swept in as part of a folder, never when a
// user deliberately selects such a file on its own.
const isFolderUploadFile = (file: File | DocumentPickerAsset) =>
  Boolean(
    (file as { webkitRelativePath?: string }).webkitRelativePath ||
    (file as { relativePath?: string }).relativePath,
  );

export type ProtectedFileHandler = (protectedFileNames: string[]) => void;

export type AddFilesOptions = {
  /** When true, checks PDF/XLSX/DOCX files for password protection and rejects them */
  blockProtectedFiles?: boolean;
  /** Callback when protected files are detected (receives array of file names) */
  onProtectedFilesDetected?: ProtectedFileHandler;
};

export const addFiles = async (
  files: (File | DocumentPickerAsset)[],
  source: string,
  uppy: FileInputProps['uppy'],
  fileMeta: FileInputProps['fileMeta'],
  referenceId?: string,
  options?: AddFilesOptions,
) => {
  try {
    let filesToAdd = files.filter(
      (file) => !(isOsMetadataFile(file.name) && isFolderUploadFile(file)),
    );

    // Check for password-protected files if enabled
    // Note: Protection checking only works on web (not native) as it requires reading file bytes
    if (options?.blockProtectedFiles && !isNative()) {
      const protectedFileNames: string[] = [];
      const nonProtectedFiles: (File | DocumentPickerAsset)[] = [];

      await Promise.all(
        files.map(async (file) => {
          const fileName = file.name;

          // Only check supported file types (PDF, XLSX, DOCX)
          if (!isSupportedForProtectionCheck(fileName)) {
            nonProtectedFiles.push(file);
            return;
          }

          const result = await checkFileProtection(file as File, fileName);

          if (result.isProtected) {
            protectedFileNames.push(fileName);
          } else {
            nonProtectedFiles.push(file);
          }
        }),
      );

      // Notify about protected files
      if (protectedFileNames.length > 0) {
        options.onProtectedFilesDetected?.(protectedFileNames);
      }

      filesToAdd = nonProtectedFiles;
    }

    // Early return if no files left to add
    if (filesToAdd.length === 0) {
      return;
    }

    const descriptors = await Promise.all(
      filesToAdd.map(async (file: File | DocumentPickerAsset) => {
        const fileName = file.name.replaceAll(' ', '_');
        const fileType = isNative() ? (file as DocumentPickerAsset).mimeType : (file as File).type;

        // eslint-disable-next-line -- native getFileData needs await.
        const fileData = await getFileData(file);
        const nameWithReferenceId = addReferenceIdToFileName(fileName, referenceId);
        return {
          source,
          name: nameWithReferenceId,
          type: fileType,
          data: fileData,
          size: fileData.size,
          meta: {
            ...fileMeta,
            // For native NFS Multipart Upload, we need to pass the uri of the file
            uri: (file as { uri: string })?.uri || '',
            // also user to upload same file to be in different folder. fileId should be different for different path
            // https://github.com/transloadit/uppy/blob/6f6686177ab716e5b3ba688a9b66809ff08518b5/packages/%40uppy/utils/src/generateFileID.js#L21
            relativePath: getRelativePath(fileMeta, file),
            referenceId: referenceId ?? '',
          },
        };
      }),
    );

    try {
      // @ts-expect-error - uppy is not null
      uppy.addFiles(descriptors);
    } catch (err) {
      uppy?.log(err as string);
    }
  } catch (err) {
    console.error('Error in addFiles:', err);
    uppy?.log(err as string);
    throw err;
  }
};
